import { useCallback, useRef } from "react";
import { useChart } from "../contexts/ChartContext";
import { calculateBasicChart, calculateDwarfChart, cancelRequest } from "../services/api";

/**
 * Custom hook for chart operations
 */
export const useChartOperations = () => {
  const {
    chartResponse,
    setChartResponse,
    error,
    setError,
    isSubmitting,
    setIsSubmitting,
  } = useChart();

  const currentRequestIdRef = useRef(null);

  const handleGenerateChart = useCallback(async (payload) => {
    // Cancel previous request if exists
    if (currentRequestIdRef.current) {
      cancelRequest(currentRequestIdRef.current);
    }

    // Generate new request ID
    const requestId = `chart-${Date.now()}`;
    currentRequestIdRef.current = requestId;

    setIsSubmitting(true);
    setError(null);
    setChartResponse(null);

    try {
      // Step 1: Get basic chart (fast - SwissEph planets)
      const basicChart = await calculateBasicChart(payload, `${requestId}-basic`);
      
      // Only update if this is still the current request
      if (currentRequestIdRef.current === requestId) {
        setChartResponse({ ...basicChart, birthData: payload });
      }

      // Step 2: Get dwarf planets (slower - Horizons API)
      // Use normalized planets from basic chart for merging
      // The backend will handle the merging and re-normalization
      const dwarfPayload = {
        ...payload,
        existingPlanets: basicChart.planets || [],
      };

      const dwarfChart = await calculateDwarfChart(dwarfPayload, `${requestId}-dwarf`);
      
      // Only update if this is still the current request
      if (currentRequestIdRef.current === requestId) {
        // Merge dwarf planets with basic chart
        const mergedChart = {
          ...basicChart,
          planets: dwarfChart.planets,
          aspects: dwarfChart.aspects,
          loading: {
            basic: false,
            dwarf: false,
          },
          birthData: payload,
        };
        setChartResponse(mergedChart);
      }

      return currentRequestIdRef.current === requestId 
        ? { ...basicChart, planets: dwarfChart.planets, aspects: dwarfChart.aspects }
        : basicChart;
    } catch (err) {
      // Only update error if this is still the current request
      if (currentRequestIdRef.current === requestId) {
        const errorMessage = err.message || "Failed to generate chart";
        setError(errorMessage);
      }
      throw err;
    } finally {
      // Only update submitting state if this is still the current request
      if (currentRequestIdRef.current === requestId) {
        setIsSubmitting(false);
        currentRequestIdRef.current = null;
      }
    }
  }, [setChartResponse, setError, setIsSubmitting]);

  return {
    chartResponse,
    error,
    isSubmitting,
    handleGenerateChart,
    setChartResponse,
    setError,
  };
};


